/**
 * http-retry
 *
 * Shared retry-with-exponential-backoff operator for HTTP calls. Extracted
 * from 4 near-identical retry({...}) blocks (quiz.service, feedback.service,
 * alert-report.service, fc-low-api.service) that all propagate TimeoutError
 * immediately and back off as base * 2^(retryCount - 1) between attempts.
 *
 * fc-session.component.ts has a 5th, superficially similar retry block that
 * does NOT retry on TimeoutError-skip and uses a different backoff exponent
 * — it is intentionally left out of this helper rather than bent to fit.
 */
import { MonoTypeOperatorFunction, throwError, timer, TimeoutError } from 'rxjs';
import { retry } from 'rxjs/operators';

export interface RetryBackoffOptions {
  readonly count: number;
  readonly baseDelayMs: number;
  /** Skip retrying on 4xx responses (client errors won't succeed on retry). */
  readonly skip4xx?: boolean;
  /**
   * Predicate evaluated before every retry; returning false propagates the error
   * immediately. Used by streaming calls to never re-POST once a partial result
   * (e.g. an NDJSON `decision`) has already been delivered.
   */
  readonly shouldRetry?: (error: unknown) => boolean;
}

export function retryWithBackoff<T>(options: RetryBackoffOptions): MonoTypeOperatorFunction<T> {
  return retry<T>({
    count: options.count,
    delay: (error: unknown, retryCount: number) => {
      if (error instanceof TimeoutError) {
        return throwError(() => error);
      }
      if (options.shouldRetry && !options.shouldRetry(error)) {
        return throwError(() => error);
      }
      if (options.skip4xx) {
        const status = (error as { status?: number } | null)?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
          return throwError(() => error);
        }
      }
      return timer(options.baseDelayMs * Math.pow(2, retryCount - 1));
    },
  });
}
